/**
 * Support Snapshot
 * Prod-safe support information display and copy functionality
 * Always visible (dev/prod/local)
 */

/* global document, navigator, Office */

import { VERSION } from "../shared/version.generated";
import { getPublicConfig, getPublicConfigSyncUnsafe } from "../excel/config/getPublicConfig";
import { diagnosticsStore } from "../shared/diagnostics";
import type { DiagnosticsSnapshot } from "../shared/diagnostics/types";

interface BackendVersionResponse {
  version: {
    appName: string;
    env: "dev" | "prod";
    gitSha: string;
    buildTime: string;
  };
}

/**
 * Format ISO timestamp for display
 */
function formatTimestamp(iso: string): string {
  try {
    return new Date(iso).toISOString();
  } catch {
    return iso;
  }
}

/**
 * Fetch backend version with timeout
 */
async function fetchBackendVersion(apiBaseUrl: string, timeoutMs: number = 3000): Promise<{ env: string; sha: string; timeMs: number } | null> {
  const startTime = performance.now();
  try {
    const url = apiBaseUrl.replace(/\/$/, "") + "/version";
    
    // Create abort controller for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    
    try {
      const response = await fetch(url, { signal: controller.signal });
      const timeMs = Math.round(performance.now() - startTime);
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        return { env: "error", sha: response.status.toString(), timeMs };
      }
      
      const data = (await response.json()) as BackendVersionResponse;
      return {
        env: data.version.env,
        sha: data.version.gitSha,
        timeMs,
      };
    } catch (fetchError) {
      clearTimeout(timeoutId);
      const timeMs = Math.round(performance.now() - startTime);
      if (fetchError instanceof Error && fetchError.name === "AbortError") {
        return { env: "error", sha: "timeout", timeMs };
      }
      const errorMsg = fetchError instanceof Error ? fetchError.message : String(fetchError);
      return { env: "error", sha: errorMsg.substring(0, 10), timeMs };
    }
  } catch (error) {
    const timeMs = Math.round(performance.now() - startTime);
    const errorMsg = error instanceof Error ? error.message : String(error);
    return { env: "error", sha: errorMsg.substring(0, 10), timeMs };
  }
}

/**
 * Get Office host and platform info
 */
function getOfficeInfo(): { host: string; platform: string } {
  try {
    if (typeof Office !== "undefined" && Office.context) {
      return {
        host: Office.context.host || "unknown",
        platform: Office.context.platform || "unknown",
      };
    }
  } catch {
    // Swallow errors
  }
  return { host: "unknown", platform: "unknown" };
}

/**
 * Get hostname from window location
 */
function getHostname(): string {
  try {
    if (typeof window !== "undefined" && window.location) {
      return window.location.hostname;
    }
  } catch {
    // Swallow errors
  }
  return "unknown";
}

/**
 * Copy text to clipboard
 */
async function copyToClipboard(text: string): Promise<boolean> {
  try {
    // Try modern clipboard API first
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
    
    // Fallback: use hidden textarea + execCommand
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.style.position = "fixed";
    textarea.style.left = "-9999px";
    textarea.style.top = "-9999px";
    document.body.appendChild(textarea);
    textarea.select();
    textarea.setSelectionRange(0, text.length);
    
    try {
      const success = document.execCommand("copy");
      document.body.removeChild(textarea);
      return success;
    } catch {
      document.body.removeChild(textarea);
      return false;
    }
  } catch (error) {
    return false;
  }
}

/**
 * Render support snapshot text
 */
function renderSupportText(
  frontendEnv: string,
  frontendSha: string,
  frontendBuildTime: string,
  hostname: string,
  apiBaseUrl: string,
  backendInfo: { env: string; sha: string; timeMs: number } | null,
  diagnostics: DiagnosticsSnapshot | null
): string {
  const lines: string[] = [];
  
  lines.push("Env: " + frontendEnv);
  lines.push("Host: " + hostname);
  lines.push("FE: " + frontendEnv + ":" + frontendSha + " (" + formatTimestamp(frontendBuildTime) + ")");
  lines.push("API: " + apiBaseUrl);
  
  if (backendInfo) {
    lines.push("BE: " + backendInfo.env + ":" + backendInfo.sha + " (" + backendInfo.timeMs + "ms)");
  } else {
    lines.push("BE: error");
  }
  
  // Always show Office info
  const officeInfo = getOfficeInfo();
  lines.push("Office: " + officeInfo.host + " / " + officeInfo.platform);
  
  // Add diagnostics info if available
  if (diagnostics) {
    if (diagnostics.last_request && diagnostics.last_request.request_id) {
      lines.push("LastRequestId: " + diagnostics.last_request.request_id);
    }
    
    if (diagnostics.last_error && diagnostics.last_error.category) {
      lines.push("LastErrorCategory: " + diagnostics.last_error.category);
    }
    
    if (diagnostics.session && diagnostics.session.started_at) {
      lines.push("SessionStarted: " + formatTimestamp(diagnostics.session.started_at));
    }
  }
  
  return lines.join("\n");
}

/**
 * Initialize support snapshot
 */
export async function initializeSupportSnapshot(): Promise<void> {
  const snapshotEl = document.getElementById("support-snapshot-text");
  const copyBtn = document.getElementById("support-copy-btn");
  const statusEl = document.getElementById("support-copy-status");
  
  if (!snapshotEl) {
    return;
  }

  // Get API base URL (with fallback)
  let apiBaseUrl = "unknown";
  try {
    const config = await getPublicConfig();
    apiBaseUrl = config.public.apiBaseUrl;
  } catch {
    // Fallback to sync unsafe
    try {
      const config = getPublicConfigSyncUnsafe();
      apiBaseUrl = config.public.apiBaseUrl;
    } catch {
      // Keep "unknown"
    }
  }

  // Get hostname
  const hostname = getHostname();

  // Initialize diagnostics store safely (must not block if it fails)
  let diagnostics: DiagnosticsSnapshot | null = null;
  try {
    await diagnosticsStore.init();
    await diagnosticsStore.mergeStatsFromStorage();
    diagnostics = diagnosticsStore.getSnapshot();
  } catch {
    // Continue without diagnostics - support snapshot must still work
  }

  // Fetch backend version (with timeout)
  const backendInfo = await fetchBackendVersion(apiBaseUrl, 3000);

  // Render initial snapshot
  const snapshotText = renderSupportText(
    VERSION.env,
    VERSION.gitSha,
    VERSION.buildTime,
    hostname,
    apiBaseUrl,
    backendInfo,
    diagnostics
  );

  snapshotEl.textContent = snapshotText;

  // Wire up copy button
  if (copyBtn) {
    copyBtn.onclick = async () => {
      if (!statusEl) {
        return;
      }

      try {
        const success = await copyToClipboard(snapshotText);
        if (success) {
          statusEl.textContent = "Copied.";
          statusEl.style.display = "block";
          setTimeout(() => {
            statusEl.style.display = "none";
          }, 2000);
        } else {
          statusEl.textContent = "Copy failed: clipboard API unavailable";
          statusEl.style.display = "block";
        }
      } catch (error) {
        const errorMsg = error instanceof Error ? error.message : String(error);
        statusEl.textContent = "Copy failed: " + errorMsg;
        statusEl.style.display = "block";
      }
    };
  }
}
